import { Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { InvitationsService } from 'core/services/invitations.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReferralModalFacade } from '../../../../shared/services/referral-modal.facade';
import { AuthService } from 'core/services/auth.service';
import { User } from 'shared/types/user.model';
import { KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { Observable, tap } from 'rxjs';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { IInvitation } from 'core/types/invitation.interface';
import { AlertComponent } from 'shared';
import { HideTooltipOnScrollDirective } from 'shared/directives';
import { NavigationService } from 'core/services/navigation.service';
import { SortOrders } from 'shared/types/sort-order.enum';
import { EmptyTemplateDirective } from 'shared/directives/empty-template.directive';
import { KorviuNoDataComponent } from 'shared/components/korviu-no-data/korviu-no-data.component';
import { ReferralActiveMissionsComponent } from './referral-active-missions/referral-active-missions.component';
import { ReferralUpdatesService } from '../../../../shared/services/referral-updates.service';

@Component({
  selector: 'app-my-referrals-my-info-default',
  imports: [
    KorviuButtonComponent,
    KorviuListComponent,
    NzTooltipDirective,
    HideTooltipOnScrollDirective,
    TranslatePipe,
    GoBackComponent,
    AlertComponent,
    EmptyTemplateDirective,
    KorviuNoDataComponent,
    ReferralActiveMissionsComponent
  ],
  templateUrl: './my-referrals-my-info-default.component.html',
  styleUrl: './my-referrals-my-info-default.component.scss'
})
export class MyReferralsMyInfoDefaultComponent implements OnInit {
  readonly referralsList = viewChild(KorviuListComponent);

  #invitationService = inject(InvitationsService);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  #referralModalFacade = inject(ReferralModalFacade);
  #authService = inject(AuthService);
  #destroyRef = inject(DestroyRef);
  #navigationService = inject(NavigationService);
  #referralUpdates = inject(ReferralUpdatesService);

  readonly currentUser = computed<User | null>(() => this.#authService.currentUser$());
  readonly noReferrals = signal(false);
  readonly showReferralEmptyState = signal(false);
  removedReferrals = signal<any[]>([]);

  seedListConfig = {
    getFn: this.#getSeedList.bind(this),
    detailsFn: this.#openSeedInfo.bind(this),
    hideSearch: false,
    doNotSortFilterSections: true,
    filterSections: [
      {
        name: '',
        localizableName: 'common.reminderFilter.title',
        parameter_name: 'reminder',
        checkboxModel: [
          {
            label: '',
            localizableName: 'common.reminderFilter.invitation',
            value: 'invitation',
            parameter_name: 'invitation'
          },
          { label: '', localizableName: 'common.reminderFilter.first', value: 'first', parameter_name: 'first' },
          { label: '', localizableName: 'common.reminderFilter.second', value: 'second', parameter_name: 'second' },
          { label: '', localizableName: 'common.reminderFilter.third', value: 'third', parameter_name: 'third' }
        ]
      }
    ],
    sortSections: [
      {
        localizableName: 'common.sortOption.invitationDate',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'common.sortOption.lastContacted',
        options: [
          {
            localizableName: 'common.sortOption.recentToOldest',
            sortDirection: { sortBy: 'last_contacted_date', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldestToRecent',
            sortDirection: { sortBy: 'last_contacted_date', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'common.sortOption.name',
        options: [
          {
            localizableName: 'common.sortOption.A-Z',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.ASCEND }
          },
          {
            localizableName: 'common.sortOption.Z-A',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.DESCEND }
          }
        ]
      }
    ],
    itemType: KorviuListItemTypes.REFERRAL,
    enableBulkRemoval: true,
    totalLocalizableName: 'buildYourNetwork.seed.header'
  };

  constructor() {
    this.#referralUpdates.changes$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.referralsList()?.reloadData(true, false));
  }

  ngOnInit(): void {
    this.#invitationService
      .getRemovedReferrals()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response: any) => {
        this.removedReferrals.set(response?.data || response?.results || []);
      });
  }

  dismissAlert(id: number): void {
    this.#invitationService
      .dismissRemovedReferral(id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.removedReferrals.update(list => list.filter(item => item.id !== id));
      });
  }

  addReferral(): void {
    this.#referralModalFacade.openAddReferralModal();
  }

  exploreMyForest(): void {
    this.#navigationService.setNextGoBackUrl('/workspace/my-referrals');
    void this.#router.navigate(['/workspace/my-forest']);
  }

  #getSeedList(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<IInvitation[]>> {
    this.showReferralEmptyState.set(false);

    return this.#invitationService.getReferralList(null, pagination, filters, sorting).pipe(
      tap(response => {
        const isUnfiltered = filters.length === 0;
        this.showReferralEmptyState.set(isUnfiltered && response.count === 0);

        if (isUnfiltered || response.count > 0) {
          this.noReferrals.set(response.count === 0);
        }
      })
    );
  }

  #openSeedInfo(seed: number): void {
    this.#router.navigate(['referral', seed], { relativeTo: this.#activatedRoute });
  }
}
